import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';

import {
  getMembersByLibrary,
  addMember,
  updateMember,
  getPaymentsByLibrary,
  getPaymentById,
  getGatewayPaymentStatus,
  getMembershipFeeConfigsByLibrary,
  getMemberCategoryOptions,
  getErrorMessage
} from '../../api/api';
import { prefetch, readPrefetched } from '../../api/prefetchCache';
import './Member.css'
import '../Reports/ReportDashboard.css'
import '../lib-page-header.css';
import '../lib-typography.css';
import '../Receipts/ReceiptPrint.css';
import { exportToCSV } from '../exportUtils';
import FilterSelect from '../components/FilterSelect';
import CollectPaymentModal from './CollectPaymentModal';
import RegisterMemberModal from './RegisterMemberModal';
import SearchableSelect from '../../shared/SearchableSelect';
import * as Constants from '../../Constant/constant';

const MEMBERS_RESOURCE = 'members';
const FEE_CONFIG_RESOURCE = 'membership-fee-configs';

// Same-render fallback only, shown until getMemberCategoryOptions (the live
// DB enum, see memberModel.getMemberCategoryOptions) resolves — the actual
// list of member categories always comes from that call (see
// memberCategoryOptions state below).
const FALLBACK_MEMBER_TYPE_OPTIONS = [
  { value: 'outsider', label: 'Outsider' },
  { value: 'school_parent', label: 'School Parent' }
];

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

const MEMBER_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'blocked', label: 'Blocked' }
];

const initialMemberForm = {
  member_type: 'outsider',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  gender: '',
  dob: '',
  address: '',
  city: '',
  state: '',
  country: 'India',
  pincode: '',
  profile_image: '',
  status: 'active',
  registration_source: 'admin_created'
};

const PAGE_SIZE = 10;

const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MemberList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sectionBase = useSectionBase();
  const libraryId = useActiveLibraryId();
  const registerTriggerRef = useRef(null);

  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);

  const [memberCategoryOptions, setMemberCategoryOptions] = useState(FALLBACK_MEMBER_TYPE_OPTIONS);
  useEffect(() => {
    let active = true;
    const result = getMemberCategoryOptions();
    // Guards against a non-promise return (e.g. an incomplete test mock) —
    // the real getMemberCategoryOptions always returns a promise.
    if (result && typeof result.then === 'function') {
      result
        .then((options) => {
          if (active && Array.isArray(options) && options.length) setMemberCategoryOptions(options);
        })
        .catch(() => {});
    }
    return () => { active = false; };
  }, []);

  // Presentation-only summary handed off by the HDFC payment callback page
  // (MembershipPaymentCallback.jsx) via navigation state — never trusted for
  // any financial decision. The backend has already verified/applied the
  // payment (or hasn't, for pending/failed) before this ever renders; this
  // only decides what modal to show and which member row it refers to.
  const [paymentResult, setPaymentResult] = useState(null);
  const [checkingPaymentStatus, setCheckingPaymentStatus] = useState(false);

  // Member row whose receipt modal is open, plus the full payment record
  // (fetched by id — getPaymentsByLibrary below doesn't carry the order
  // number/gateway reference the receipt needs) it's showing.
  const [receiptTarget, setReceiptTarget] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null); // member row awaiting a collected payment
  const [editingMemberId, setEditingMemberId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const [memberForm, setMemberForm] = useState(initialMemberForm);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  const [csvRows, setCsvRows] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');

  const fetchMembers = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);
      const data = await getMembersByLibrary(libraryId);
      setMembers(Array.isArray(data) ? data : []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetched only to know which members have a successful payment to print a
  // receipt for — the "Print Receipt" action button below needs the real
  // payment id to hand off to the Receipt Print page.
  const fetchPayments = async () => {
    try {
      if (!libraryId) return;

      const data = await getPaymentsByLibrary(libraryId);
      setPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      setPayments([]);
    }
  };

  useEffect(() => {
    // Seed instantly from a hover-triggered sidebar prefetch if warm, then
    // always still run the real fetches — member/payment data is never
    // trusted from a cache older than this.
    const warmMembers = readPrefetched(MEMBERS_RESOURCE, libraryId);
    if (warmMembers) setMembers(Array.isArray(warmMembers) ? warmMembers : []);

    fetchMembers();
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  // Warms the fee-config lookup both Register Member and Collect Payment
  // need, the moment the user shows intent to open either — never on mount.
  const prefetchFeeConfig = () => {
    if (!libraryId) return;
    prefetch(FEE_CONFIG_RESOURCE, libraryId, () => getMembershipFeeConfigsByLibrary(libraryId)).catch(() => {});
  };

  // Detects the HDFC callback page's hand-off (paymentResult in navigation
  // state), refreshes the lists so the modal below has current data to show,
  // and immediately strips the state out of history — otherwise refreshing
  // this page or navigating back here would re-trigger the same modal.
  useEffect(() => {
    const result = location.state?.paymentResult;
    if (!result) return;

    setPaymentResult(result);
    fetchMembers();
    fetchPayments();

    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Payments are ordered newest-first by the API, so the first match here is
  // already that member's most recent successful transaction.
  const getSuccessfulPayment = (member) => {
    return payments.find(
      (payment) =>
        String(payment.member_id) === String(member.id) &&
        String(payment.payment_status || '').toLowerCase() === 'success'
    );
  };

  // Opens the receipt modal in place — never navigates away from Members —
  // and loads that payment's full record (order number, HDFC gateway
  // reference, etc.) from the backend.
  const handlePrintReceipt = async (member) => {
    const payment = getSuccessfulPayment(member);
    if (!payment) return;

    setReceiptTarget(member);
    setReceiptData(null);
    setReceiptError('');
    setReceiptLoading(true);

    try {
      const full = await getPaymentById(payment.id);
      setReceiptData(full);
    } catch (error) {
      setReceiptError(getErrorMessage(error));
    } finally {
      setReceiptLoading(false);
    }
  };

  const closeReceiptModal = () => {
    setReceiptTarget(null);
    setReceiptData(null);
    setReceiptError('');
  };

  const handlePrintReceiptNow = () => window.print();

  // The member row this payment result refers to — resolved from the
  // just-refreshed members list, never from the navigation state itself.
  const paymentResultMember = paymentResult
    ? members.find((m) => String(m.membership_id) === String(paymentResult.membershipId))
    : null;

  const closePaymentResultModal = () => setPaymentResult(null);

  // Re-asks the backend for the real, re-verified status — never flips the
  // modal's outcome based on anything client-side. A resolved success also
  // refreshes the lists so the member's row (and the Print Receipt button)
  // reflects the newly-activated membership.
  const handleModalCheckStatus = async () => {
    if (!paymentResult?.orderNumber) return;

    setCheckingPaymentStatus(true);

    try {
      const result = await getGatewayPaymentStatus(paymentResult.orderNumber);
      const nextStatus = String(result?.status || 'pending').toLowerCase();

      setPaymentResult((prev) => ({
        ...prev,
        status: nextStatus,
        amount: result?.amount ?? prev?.amount,
        gatewayReference: result?.gateway_reference ?? prev?.gatewayReference,
        membershipId: result?.membership_id ?? prev?.membershipId
      }));

      if (nextStatus === 'success') {
        fetchMembers();
        fetchPayments();
      }
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setCheckingPaymentStatus(false);
    }
  };

  // Both "Collect Payment" (pending) and "Retry Payment" (failed) reuse the
  // exact same CollectPaymentModal flow already used from the table's own
  // action button — a new HDFC order is only ever created if the user
  // explicitly submits that modal, never automatically on redirect/refresh/
  // modal open.
  const handlePaymentResultCollect = () => {
    if (!paymentResultMember) return;
    setPaymentTarget(paymentResultMember);
    setPaymentResult(null);
  };

  const getStatusBadge = (status) => {
    if (status === 'active') return 'bg-success';
    if (status === 'inactive') return 'bg-secondary';
    if (status === 'blocked') return 'bg-danger';
    if (status === 'pending') return 'bg-warning text-dark';
    return 'bg-light text-dark';
  };

  const getMembershipStatusBadge = (status) => {
    if (status === 'active') return 'bg-success';
    if (status === 'pending_payment') return 'bg-warning text-dark';
    return 'bg-light text-dark';
  };

  // What's still owed on this membership's security deposit — the button
  // only ever appears when this is > 0 and the membership hasn't paid yet.
  const getAmountDue = (member) => {
    return Math.max(Number(member.security_deposit || 0) - Number(member.deposit_balance || 0), 0);
  };

  const canCollectPayment = (member) => {
    return member.membership_status === 'pending_payment' && getAmountDue(member) > 0 && member.membership_id;
  };

  const getMemberName = (member) => {
    return (
      member.full_name ||
      member.member_name ||
      member.parent_name ||
      `${member.first_name || ''} ${member.last_name || ''}`.trim() ||
      '-'
    );
  };

  const getMemberMobile = (member) => {
    return member.mobile || member.phone || '-';
  };

  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      const keyword = search.toLowerCase();

      const matchSearch =
        String(getMemberName(member)).toLowerCase().includes(keyword) ||
        String(member.mobile || member.phone || '').toLowerCase().includes(keyword) ||
        String(member.email || '').toLowerCase().includes(keyword) ||
        String(member.member_type || '').toLowerCase().includes(keyword) ||
        String(member.membership_no || '').toLowerCase().includes(keyword);

      const matchStatus = statusFilter
        ? String(member.status || '') === statusFilter
        : true;

      const matchType = typeFilter
        ? String(member.member_type || '') === typeFilter
        : true;

      return matchSearch && matchStatus && matchType;
    });
  }, [members, search, statusFilter, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / PAGE_SIZE));

  const paginatedMembers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredMembers.slice(start, start + PAGE_SIZE);
  }, [filteredMembers, page]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, typeFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const stats = useMemo(() => {
    const total = members.length;
    const active = members.filter((m) => m.status === 'active').length;
    const pending = members.filter((m) => m.status === 'pending').length;
    const outsiders = members.filter((m) => m.member_type === 'outsider').length;
    const schoolParents = members.filter((m) => m.member_type === 'school_parent').length;

    return {
      total,
      active,
      pending,
      outsiders,
      schoolParents
    };
  }, [members]);

  const openEditModal = (member) => {
    setEditingMemberId(member.id);
    setMemberForm({
      member_type: member.member_type || 'outsider',
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      phone: member.phone || member.mobile || '',
      password: '',
      gender: member.gender || '',
      dob: member.dob ? String(member.dob).slice(0, 10) : '',
      address: member.address || '',
      city: member.city || '',
      state: member.state || '',
      country: member.country || 'India',
      pincode: member.pincode || '',
      profile_image: member.profile_image || '',
      status: member.status || 'active',
      registration_source: member.registration_source || 'admin_created'
    });
    setProfileImageFile(null);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (saving) return;
    setShowAddModal(false);
    setEditingMemberId(null);
    setMemberForm(initialMemberForm);
    setProfileImageFile(null);
  };

  const handleProfileImageChange = (e) => {
    setProfileImageFile(e.target.files?.[0] || null);
  };

  useEffect(() => {
    if (!profileImageFile) {
      setProfileImagePreview(null);
      return;
    }

    const url = URL.createObjectURL(profileImageFile);
    setProfileImagePreview(url);

    return () => URL.revokeObjectURL(url);
  }, [profileImageFile]);

  const handleToggleStatus = async (member) => {
    const newStatus = member.status === 'active' ? 'inactive' : 'active';

    const confirmed = await Swal.fire({
      title: `Mark "${getMemberName(member)}" as ${newStatus}?`,
      text:
        newStatus === 'inactive'
          ? 'Inactive members cannot issue or reserve books.'
          : 'This member will be able to issue/reserve books again.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, mark ${newStatus}`,
      cancelButtonText: 'Cancel'
    });

    if (!confirmed.isConfirmed) return;

    try {
      // The update endpoint is a full update — it requires library_id,
      // member_type, first_name and phone, so send the whole record (not just
      // the new status) or the API rejects with a 400.
      await updateMember(member.id, {
        library_id: libraryId,
        member_type: member.member_type,
        first_name: member.first_name,
        last_name: member.last_name || null,
        email: member.email || null,
        phone: member.phone || member.mobile,
        status: newStatus
      });
      Swal.fire('Success', `Member marked as ${newStatus}`, 'success');
      fetchMembers();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const openImportModal = () => {
    setCsvRows([]);
    setCsvFileName('');
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    if (importing) return;
    setShowImportModal(false);
    setCsvRows([]);
    setCsvFileName('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setMemberForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const validateMemberForm = () => {
    if (!libraryId) {
      Swal.fire('Error', 'Library ID not found', 'error');
      return false;
    }

    if (!memberForm.first_name.trim()) {
      Swal.fire('Error', 'First name is required', 'error');
      return false;
    }

    if (!memberForm.phone.trim()) {
      Swal.fire('Error', 'Mobile number is required', 'error');
      return false;
    }

    if (!PHONE_REGEX.test(memberForm.phone.trim())) {
      Swal.fire('Error', 'Enter a valid 10-digit mobile number', 'error');
      return false;
    }

    if (memberForm.email?.trim() && !EMAIL_REGEX.test(memberForm.email.trim())) {
      Swal.fire('Error', 'Enter a valid email address', 'error');
      return false;
    }

    if (!memberForm.member_type) {
      Swal.fire('Error', 'Member type is required', 'error');
      return false;
    }

    return true;
  };

  // This modal is edit-only now — new members are always created through the
  // unified Register Member flow (which also creates the membership + fee
  // config-driven deposit), reached via the "+ Register Member" button
  // above. editingMemberId is always set by the time this fires.
  const handleEditMember = async (e) => {
    e.preventDefault();

    if (!validateMemberForm()) return;

    const payload = {
      library_id: libraryId,
      member_type: memberForm.member_type,
      first_name: memberForm.first_name.trim(),
      last_name: memberForm.last_name?.trim() || null,
      email: memberForm.email?.trim() || null,
      phone: memberForm.phone.trim(),
      gender: memberForm.gender || null,
      dob: memberForm.dob || null,
      address: memberForm.address || null,
      city: memberForm.city || null,
      state: memberForm.state || null,
      country: memberForm.country || null,
      pincode: memberForm.pincode || null,
      profile_image: memberForm.profile_image || null,
      is_email_verified: memberForm.email ? 1 : 0,
      is_phone_verified: 1,
      registration_source: memberForm.registration_source || 'admin_created',
      status: memberForm.status || 'active'
    };

    if (memberForm.password.trim()) {
      payload.password = memberForm.password;
    }

    // Only switch to multipart when a new photo was actually picked — the
    // common (no photo change) case keeps sending plain JSON exactly as
    // before.
    let finalPayload = payload;

    if (profileImageFile) {
      const fd = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        fd.append(key, value ?? '');
      });
      fd.append('profile_image', profileImageFile);
      finalPayload = fd;
    }

    try {
      setSaving(true);
      await updateMember(editingMemberId, finalPayload);
      Swal.fire('Success', 'Member updated successfully', 'success');
      closeAddModal();
      fetchMembers();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = [
      [
        'first_name',
        'last_name',
        'email',
        'phone',
        'password',
        'member_type',
        'gender',
        'dob',
        'address',
        'city',
        'state',
        'country',
        'pincode',
        'status'
      ].join(','),
      [
        'Ravi',
        'Sharma',
        'ravi@example.com',
        '9876543210',
        'Ravi@123',
        'outsider',
        'male',
        '1990-01-01',
        'Sector 1',
        'Noida',
        'Uttar Pradesh',
        'India',
        '201301',
        'active'
      ].join(','),
      [
        'Priya',
        'Verma',
        'priya@example.com',
        '9876543211',
        'Priya@123',
        'school_parent',
        'female',
        '1992-02-02',
        'Sector 2',
        'Noida',
        'Uttar Pradesh',
        'India',
        '201301',
        'active'
      ].join(',')
    ].join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'member_import_template.csv';
    link.click();

    URL.revokeObjectURL(url);
  };

  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"' && insideQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === ',' && !insideQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());

    return result;
  };

  const parseCsv = (text) => {
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length < 2) return [];

    const headers = parseCsvLine(lines[0]).map((h) => h.trim());

    return lines.slice(1).map((line, index) => {
      const values = parseCsvLine(line);
      const row = {
        __rowNo: index + 2
      };

      headers.forEach((header, i) => {
        row[header] = values[i] || '';
      });

      return row;
    });
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      Swal.fire('Error', 'Please upload CSV file only', 'error');
      return;
    }

    setCsvFileName(file.name);

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const rows = parseCsv(text);

        if (!rows.length) {
          Swal.fire('Error', 'CSV file is empty or invalid', 'error');
          return;
        }

        setCsvRows(rows);
      } catch (error) {
        Swal.fire('Error', 'Failed to read CSV file', 'error');
      }
    };

    reader.readAsText(file);
  };

  const validateImportRow = (row) => {
    if (!row.first_name) {
      return `Row ${row.__rowNo}: first_name is required`;
    }

    if (!row.phone) {
      return `Row ${row.__rowNo}: phone is required`;
    }

    if (!row.password) {
      return `Row ${row.__rowNo}: password is required`;
    }

    if (!row.member_type) {
      return `Row ${row.__rowNo}: member_type is required`;
    }

    const validCategoryValues = memberCategoryOptions.map((option) => option.value);
    if (!validCategoryValues.includes(row.member_type)) {
      return `Row ${row.__rowNo}: member_type should be one of: ${validCategoryValues.join(', ')}`;
    }

    return null;
  };

  const handleBulkImport = async () => {
    if (!libraryId) {
      Swal.fire('Error', 'Library ID not found', 'error');
      return;
    }

    if (!csvRows.length) {
      Swal.fire('Error', 'Please upload CSV first', 'error');
      return;
    }

    const validationErrors = csvRows
      .map(validateImportRow)
      .filter(Boolean);

    if (validationErrors.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'CSV Validation Error',
        html: validationErrors.slice(0, 10).join('<br/>')
      });
      return;
    }

    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Import Members?',
      html: `You are importing <b>${csvRows.length}</b> members.`,
      showCancelButton: true,
      confirmButtonText: 'Yes, Import',
      cancelButtonText: 'Cancel'
    });

    if (!confirm.isConfirmed) return;

    try {
      setImporting(true);

      let successCount = 0;
      const failedRows = [];

      for (const row of csvRows) {
        const payload = {
          library_id: libraryId,
          member_type: row.member_type || 'outsider',
          first_name: row.first_name,
          last_name: row.last_name || null,
          email: row.email || null,
          phone: row.phone,
          password: row.password,
          gender: row.gender || null,
          dob: row.dob || null,
          address: row.address || null,
          city: row.city || null,
          state: row.state || null,
          country: row.country || 'India',
          pincode: row.pincode || null,
          profile_image: null,
          is_email_verified: row.email ? 1 : 0,
          is_phone_verified: 1,
          registration_source: 'bulk_import',
          status: row.status || 'active'
        };

        try {
          await addMember(payload);
          successCount += 1;
        } catch (error) {
          failedRows.push({
            row: row.__rowNo,
            name: row.first_name,
            error: getErrorMessage(error)
          });
        }
      }

      await fetchMembers();

      if (failedRows.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Import Completed With Errors',
          html: `
            <div class="text-start">
              <p><b>Success:</b> ${successCount}</p>
              <p><b>Failed:</b> ${failedRows.length}</p>
              <hr/>
              ${failedRows
                .slice(0, 10)
                .map((item) => `Row ${item.row} (${item.name}): ${item.error}`)
                .join('<br/>')}
            </div>
          `
        });
      } else {
        Swal.fire('Success', `${successCount} members imported successfully`, 'success');
      }

      closeImportModal();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setImporting(false);
    }
  };

  const exportColumns = [
    { header: 'Name', key: '__name' },
    { header: 'Mobile', key: '__mobile' },
    { header: 'Email', key: 'email' },
    { header: 'Type', key: 'member_type' },
    { header: 'Membership No', key: 'membership_no' },
    { header: 'Status', key: 'status' }
  ];

  const exportRows = filteredMembers.map((m) => ({
    ...m,
    __name: getMemberName(m),
    __mobile: getMemberMobile(m)
  }));

  const handleExportCSV = () => exportToCSV(exportColumns, exportRows, 'members_list');

  const getInitials = (member) => {
    const name = getMemberName(member);
    if (!name || name === '-') return '?';
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  };

  const hasFilters = Boolean(search || typeFilter || statusFilter);

  return (
    <div className="container-fluid mt-3 mem-page">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-users" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Members</h3>
            <div className="lib-page-header__subtitle">View, add and import school parent / outsider members</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button
            type="button"
            className="btn btn-light"
            onClick={fetchMembers}
            disabled={loading}
          >
            <i className="fas fa-rotate-right me-1" />
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={openImportModal}
          >
            <i className="fas fa-file-import me-1" />
            Bulk Import
          </button>
          <button type="button" className="btn btn-light" onClick={handleExportCSV}>
            <i className="fas fa-file-csv me-1" />
            CSV
          </button>
          <button
            type="button"
            className="btn btn-primary"
            ref={registerTriggerRef}
            onClick={() => setShowRegisterModal(true)}
            onMouseEnter={prefetchFeeConfig}
            onFocus={prefetchFeeConfig}
          >
            + Register Member
          </button>
        </div>
      </div>

      <div className="report-grid mb-3">
        <div className="report-card" style={{ '--accent': '#6f8fe6' }}>
          <span className="report-card__icon" style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}><i className="fas fa-users" /></span>
          <span className="report-card__title">Total Members</span>
          <span className="report-card__value">{stats.total}</span>
        </div>

        <div className="report-card" style={{ '--accent': '#22b07d' }}>
          <span className="report-card__icon" style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}><i className="fas fa-user-check" /></span>
          <span className="report-card__title">Active</span>
          <span className="report-card__value">{stats.active}</span>
        </div>

        <div className="report-card" style={{ '--accent': '#f5a524' }}>
          <span className="report-card__icon" style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}><i className="fas fa-user-tag" /></span>
          <span className="report-card__title">Outsiders</span>
          <span className="report-card__value">{stats.outsiders}</span>
        </div>

        <div className="report-card" style={{ '--accent': '#7c5cfc' }}>
          <span className="report-card__icon" style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}><i className="fas fa-user-friends" /></span>
          <span className="report-card__title">School Parents</span>
          <span className="report-card__value">{stats.schoolParents}</span>
        </div>
      </div>

      <div className="card shadow-sm mt-3 table-full-card">
        <div className="card-body">
          <div className="mem-toolbar">
            <div className="mem-search">
              <i className="fas fa-search" />
              <input
                type="text"
                placeholder="Search member, mobile, email, membership no..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="mem-search__clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>

            <FilterSelect
              value={typeFilter}
              onChange={setTypeFilter}
              options={[{ value: '', label: 'All Types' }, ...memberCategoryOptions]}
            />

            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: '', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'pending', label: 'Pending' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'blocked', label: 'Blocked' },
              ]}
            />

            <button
              type="button"
              className="mem-reset-btn"
              onClick={() => {
                setSearch('');
                setTypeFilter('');
                setStatusFilter('');
              }}
              disabled={!hasFilters}
            >
              <i className="fas fa-rotate-left me-1" />
              Reset
            </button>

            <span className="mem-toolbar__count">
              {filteredMembers.length} {filteredMembers.length === 1 ? 'member' : 'members'}
            </span>
          </div>

          {showLoader ? (
            <Loader />
          ) : (
            <div className="table-responsive mem-table-wrap">
              <table className="table table-hover align-middle mem-table lib-table-cell">
                <thead className="table-light lib-table-header">
                  <tr>
                    <th className="mem-col-no">#</th>
                    <th>Member</th>
                    <th>Contact</th>
                    <th>Type</th>
                    <th>Membership Status</th>
                    <th>Deposit</th>
                    <th>Member Status</th>
                    <th>Created</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedMembers.length > 0 ? (
                    paginatedMembers.map((member, index) => (
                      <tr key={member.id}>
                        <td className="mem-col-no">{(page - 1) * PAGE_SIZE + index + 1}</td>

                        <td data-label="Member">
                          <div className="mem-cell">
                            {(member.profile_image || member.photo) ? (
                              <img
                                src={`${Constants.ROOT_URL}/${String(member.profile_image || member.photo).replace(/^\/+/, '')}`}
                                alt=""
                                className="mem-avatar mem-avatar--photo"
                              />
                            ) : (
                              <span
                                className={`mem-avatar ${
                                  member.member_type === 'school_parent'
                                    ? 'mem-avatar--parent'
                                    : 'mem-avatar--outsider'
                                }`}
                              >
                                {getInitials(member)}
                              </span>
                            )}
                            <div className="mem-cell__text">
                              <span className="mem-name">{getMemberName(member)}</span>
                              <span className="mem-sub">
                                {member.membership_no
                                  ? `Membership No: ${member.membership_no}`
                                  : 'No membership record'}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td data-label="Contact">
                          <div className="mem-contact">
                            <i className="fas fa-phone" />
                            {getMemberMobile(member)}
                          </div>
                          <div className="mem-contact mem-sub">
                            <i className="fas fa-envelope" />
                            {member.email || '-'}
                          </div>
                        </td>

                        <td data-label="Type">
                          <span
                            className={`mem-pill ${
                              member.member_type === 'school_parent'
                                ? 'mem-pill--parent'
                                : 'mem-pill--outsider'
                            } lib-status-badge`}
                          >
                            {String(member.member_type || '-').replace('_', ' ')}
                          </span>
                        </td>

                        <td data-label="Membership Status">
                          {member.membership_status ? (
                            <span className={`badge ${getMembershipStatusBadge(member.membership_status)} lib-status-badge`}>
                              {member.membership_status === 'pending_payment' ? 'Pending Payment' : member.membership_status}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>

                        <td data-label="Deposit">
                          {member.membership_id ? (
                            member.membership_status === 'pending_payment' ? (
                              <>
                                <div>Required: ₹{Number(member.security_deposit || 0)}</div>
                                <div className="small text-muted">Paid: ₹{Number(member.deposit_balance || 0)}</div>
                                <div className="small text-danger">Due: ₹{getAmountDue(member)}</div>
                              </>
                            ) : (
                              <>₹ {Number(member.deposit_balance || 0)}</>
                            )
                          ) : (
                            '-'
                          )}
                        </td>

                        <td data-label="Member Status">
                          <span className={`badge ${getStatusBadge(member.status)} lib-status-badge`}>
                            {member.status || '-'}
                          </span>
                        </td>

                        <td className="mem-sub" data-label="Created">
                          {member.created_at
                            ? String(member.created_at).slice(0, 10)
                            : '-'}
                        </td>

                        <td data-label="Action">
                          <div className="mem-actions">
                            <button
                              type="button"
                              className="mem-action-btn mem-action-btn--edit"
                              onClick={() => openEditModal(member)}
                            >
                              <i className="fas fa-pen" />
                              Edit
                            </button>

                            {/* A pending_payment membership isn't manually activated —
                                it only becomes active once its deposit is collected. */}
                            {member.membership_status !== 'pending_payment' && (
                              <button
                                type="button"
                                className={`mem-action-btn ${
                                  member.status === 'active'
                                    ? 'mem-action-btn--warn'
                                    : 'mem-action-btn--ok'
                                }`}
                                onClick={() => handleToggleStatus(member)}
                              >
                                <i
                                  className={`fas ${
                                    member.status === 'active' ? 'fa-ban' : 'fa-check'
                                  }`}
                                />
                                {member.status === 'active' ? 'Deactivate' : 'Activate'}
                              </button>
                            )}

                            {canCollectPayment(member) && (
                              <button
                                type="button"
                                className="mem-action-btn mem-action-btn--danger"
                                onClick={() => setPaymentTarget(member)}
                                onMouseEnter={prefetchFeeConfig}
                                onFocus={prefetchFeeConfig}
                              >
                                <i className="fas fa-rupee-sign" />
                                Collect Payment
                              </button>
                            )}

                            {getSuccessfulPayment(member) && (
                              <button
                                type="button"
                                className="mem-action-btn mem-action-btn--edit"
                                onClick={() => handlePrintReceipt(member)}
                                title="Print this member's payment receipt"
                              >
                                <i className="fas fa-print" />
                                Print Receipt
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9">
                        <div className="mem-empty">
                          <span className="mem-empty__icon">
                            <i className="fas fa-users-slash" />
                          </span>
                          <span className="mem-empty__title">No members found</span>
                          <span className="mem-empty__hint">
                            {hasFilters
                              ? 'Try clearing the search or filters above.'
                              : 'Add a member or import them in bulk from a CSV file.'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {filteredMembers.length > 0 && (
                <div className="mem-pagination">
                  <div className="mem-pagination__info">
                    Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                    <span> · {filteredMembers.length} members</span>
                  </div>

                  <div className="mem-pagination__nav">
                    <button
                      type="button"
                      className="mem-page-btn"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <i className="fas fa-chevron-left" />
                      Previous
                    </button>

                    <button
                      type="button"
                      className="mem-page-btn"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                      <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

{showAddModal && (
  <div className="fine-popup-overlay">
    <div className="fine-popup-box fine-popup-box--member">
      <form onSubmit={handleEditMember}>
        <div className="fine-popup-header">
          <h5 className="mb-0">
            Edit Member
          </h5>

          <button
            type="button"
            className="fine-popup-close"
            onClick={closeAddModal}
            disabled={saving}
          >
            ×
          </button>
        </div>

        <div className="fine-popup-body">
          <div className="fine-member-grid">

            <div className="fine-field fine-field--full mem-photo-field">
              <label className="form-label">Photo</label>
              <div className="mem-photo-row">
                {(profileImagePreview || memberForm.profile_image) && (
                  <img
                    src={
                      profileImagePreview ||
                      `${Constants.ROOT_URL}/${String(memberForm.profile_image).replace(/^\/+/, '')}`
                    }
                    alt="Profile preview"
                    className="mem-photo-preview"
                  />
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="form-control"
                  onChange={handleProfileImageChange}
                />
              </div>
            </div>

            <div className="fine-field">
              <label className="form-label">Member Type *</label>
              <SearchableSelect
                name="member_type"
                value={memberForm.member_type}
                onChange={handleFormChange}
                options={memberCategoryOptions}
                required
                clearable={false}
              />
            </div>

            <div className="fine-field">
              <label className="form-label">First Name *</label>
              <input
                type="text"
                name="first_name"
                className="form-control"
                value={memberForm.first_name}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="fine-field">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                name="last_name"
                className="form-control"
                value={memberForm.last_name}
                onChange={handleFormChange}
              />
            </div>

            <div className="fine-field">
              <label className="form-label">Mobile *</label>
              <input
                type="text"
                name="phone"
                className="form-control"
                value={memberForm.phone}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="fine-field">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={memberForm.email}
                onChange={handleFormChange}
              />
            </div>

            <div className="fine-field">
              <label className="form-label">
                Password {editingMemberId ? '' : '*'}
              </label>
              <input
                type="password"
                name="password"
                className="form-control"
                value={memberForm.password}
                onChange={handleFormChange}
                placeholder={editingMemberId ? 'Leave blank to keep current' : ''}
                required={!editingMemberId}
              />
            </div>

            <div className="fine-field">
              <label className="form-label">Gender</label>
              <SearchableSelect
                name="gender"
                value={memberForm.gender}
                onChange={handleFormChange}
                options={GENDER_OPTIONS}
                placeholder="Select Gender"
              />
            </div>

            <div className="fine-field">
              <label className="form-label">DOB</label>
              <input
                type="date"
                name="dob"
                className="form-control"
                value={memberForm.dob}
                onChange={handleFormChange}
              />
            </div>

            <div className="fine-field">
              <label className="form-label">Status</label>
              <SearchableSelect
                name="status"
                value={memberForm.status}
                onChange={handleFormChange}
                options={MEMBER_STATUS_OPTIONS}
                clearable={false}
              />
            </div>

            <div className="fine-field fine-field--full">
              <label className="form-label">Address</label>
              <textarea
                name="address"
                rows="2"
                className="form-control"
                value={memberForm.address}
                onChange={handleFormChange}
              />
            </div>

            <div className="fine-field">
              <label className="form-label">City</label>
              <input
                type="text"
                name="city"
                className="form-control"
                value={memberForm.city}
                onChange={handleFormChange}
              />
            </div>

            <div className="fine-field">
              <label className="form-label">State</label>
              <input
                type="text"
                name="state"
                className="form-control"
                value={memberForm.state}
                onChange={handleFormChange}
              />
            </div>

            <div className="fine-field">
              <label className="form-label">Country</label>
              <input
                type="text"
                name="country"
                className="form-control"
                value={memberForm.country}
                onChange={handleFormChange}
              />
            </div>

            <div className="fine-field">
              <label className="form-label">Pincode</label>
              <input
                type="text"
                name="pincode"
                className="form-control"
                value={memberForm.pincode}
                onChange={handleFormChange}
              />
            </div>

          </div>
        </div>

        <div className="fine-popup-footer">
          <button
            type="button"
            className="btn btn-light"
            onClick={closeAddModal}
            disabled={saving}
          >
            Cancel
          </button>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : editingMemberId ? 'Update Member' : 'Save Member'}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

      {showImportModal && (
        <div className="fine-popup-overlay">
          <div className="fine-popup-box" style={{ width: 660 }}>

            {/* Header */}
            <div className="fine-popup-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <i className="fas fa-file-import" style={{ fontSize: 17 }} />
                <h5 className="mb-0">Bulk Import Members</h5>
              </div>
              <button
                type="button"
                className="fine-popup-close"
                onClick={closeImportModal}
                disabled={importing}
              >
                ×
              </button>
            </div>

            <div className="fine-popup-body">

              {/* Column info */}
              <div style={{
                background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
                border: '1px solid #bfdbfe', borderRadius: 10,
                padding: '14px 16px', marginBottom: 18
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <i className="fas fa-info-circle" style={{ color: '#2563eb', fontSize: 16, marginTop: 2, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: '#1e40af', fontSize: 12, marginBottom: 7, textTransform: 'uppercase', letterSpacing: .4 }}>
                      Required columns
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                      {['first_name', 'phone', 'password', 'member_type'].map((col) => (
                        <span key={col} style={{
                          background: '#2563eb', color: '#fff',
                          padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600
                        }}>{col}</span>
                      ))}
                    </div>
                    <div style={{ fontWeight: 600, color: '#64748b', fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: .4 }}>
                      Optional columns
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                      last_name, email, gender, dob, address, city, state, country, pincode, status
                    </div>
                  </div>
                </div>
              </div>

              {/* Upload section */}
              <div style={{
                border: '1px solid #e2e8f0', borderRadius: 10,
                padding: '16px', marginBottom: 16, background: '#f8fafc'
              }}>
                <div style={{ fontWeight: 700, color: '#374151', fontSize: 13, marginBottom: 12 }}>
                  <i className="fas fa-upload me-2" style={{ color: '#2563eb' }} />
                  Upload CSV File
                </div>
                <div className="input-group">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={downloadTemplate}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <i className="fas fa-download me-1" />Download Template
                  </button>
                  <input
                    type="file"
                    accept=".csv"
                    className="form-control"
                    onChange={handleCsvUpload}
                  />
                </div>
                {csvFileName && (
                  <div style={{
                    marginTop: 10, fontSize: 13,
                    display: 'flex', alignItems: 'center', gap: 6, color: '#16a34a', fontWeight: 600
                  }}>
                    <i className="fas fa-check-circle" />
                    <span>{csvFileName}</span>
                  </div>
                )}
              </div>

              {/* Preview table */}
              {csvRows.length > 0 && (
                <div>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#374151' }}>
                      <i className="fas fa-table me-1" style={{ color: '#2563eb' }} />Preview
                    </span>
                    <span style={{
                      background: '#dcfce7', color: '#166534',
                      padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600
                    }}>
                      {csvRows.length} rows ready
                    </span>
                  </div>

                  <div className="table-responsive" style={{
                    maxHeight: 220, borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'auto'
                  }}>
                    <table className="table table-sm table-hover mb-0 align-middle">
                      <thead style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          {['Row', 'Name', 'Phone', 'Email', 'Type', 'Status'].map((h) => (
                            <th key={h} style={{ color: '#fff', fontWeight: 600, fontSize: 12, padding: '8px 10px', border: 'none' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows.slice(0, 10).map((row) => (
                          <tr key={row.__rowNo}>
                            <td style={{ fontSize: 12, color: '#94a3b8', width: 40 }}>{row.__rowNo}</td>
                            <td style={{ fontSize: 13, fontWeight: 600 }}>{row.first_name} {row.last_name}</td>
                            <td style={{ fontSize: 12 }}>{row.phone}</td>
                            <td style={{ fontSize: 12, color: '#64748b' }}>{row.email || '-'}</td>
                            <td>
                              <span style={{
                                background: row.member_type === 'school_parent' ? '#eff6ff' : '#f0fdf4',
                                color: row.member_type === 'school_parent' ? '#1d4ed8' : '#16a34a',
                                padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap'
                              }}>
                                {String(row.member_type || '-').replace('_', ' ')}
                              </span>
                            </td>
                            <td>
                              <span style={{
                                background: '#f0fdf4', color: '#16a34a',
                                padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600
                              }}>
                                {row.status || 'active'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {csvRows.length > 10 && (
                    <small style={{ color: '#94a3b8', fontSize: 11, marginTop: 4, display: 'block' }}>
                      Showing first 10 of {csvRows.length} rows
                    </small>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="fine-popup-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={closeImportModal}
                disabled={importing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleBulkImport}
                disabled={importing || csvRows.length === 0}
              >
                {importing ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Importing...</>
                ) : (
                  <><i className="fas fa-file-import me-1" />Import Members</>
                )}
              </button>
            </div>

          </div>
        </div>
      )}

      {paymentTarget && (
        <CollectPaymentModal
          memberId={paymentTarget.id}
          membershipId={paymentTarget.membership_id}
          amount={getAmountDue(paymentTarget)}
          memberName={getMemberName(paymentTarget)}
          membershipNo={paymentTarget.membership_no}
          purpose="Membership Security Deposit"
          requiredAmount={Number(paymentTarget.security_deposit || 0)}
          paidAmount={Number(paymentTarget.deposit_balance || 0)}
          onClose={() => setPaymentTarget(null)}
          onSuccess={() => {
            setPaymentTarget(null);
            fetchMembers();
          }}
        />
      )}

      {paymentResult && (
        <div className="fine-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && closePaymentResultModal()}>
          <div className="fine-popup-box" style={{ width: 440 }}>
            <div
              className="fine-popup-header"
              style={
                paymentResult.status === 'success'
                  ? { background: 'linear-gradient(135deg, #16a34a, #15803d)' }
                  : paymentResult.status === 'failed' || paymentResult.status === 'cancelled'
                    ? { background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }
                    : undefined
              }
            >
              <h5 className="mb-0">
                {paymentResult.status === 'success'
                  ? 'Payment Successful'
                  : paymentResult.status === 'failed'
                    ? 'Payment Failed'
                    : paymentResult.status === 'cancelled'
                      ? 'Payment Cancelled'
                      : 'Payment Pending'}
              </h5>
              <button type="button" className="fine-popup-close" onClick={closePaymentResultModal}>
                ×
              </button>
            </div>

            <div className="fine-popup-body">
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <i
                  className={`fas ${
                    paymentResult.status === 'success'
                      ? 'fa-check-circle'
                      : paymentResult.status === 'failed' || paymentResult.status === 'cancelled'
                        ? 'fa-times-circle'
                        : 'fa-clock'
                  }`}
                  style={{
                    fontSize: 44,
                    color:
                      paymentResult.status === 'success'
                        ? '#16a34a'
                        : paymentResult.status === 'failed' || paymentResult.status === 'cancelled'
                          ? '#dc2626'
                          : '#d97706'
                  }}
                />
              </div>

              <dl style={{ display: 'grid', gridTemplateColumns: '150px 1fr', rowGap: 10, margin: '0 0 12px' }}>
                <dt style={{ fontSize: 12.5, color: '#64748b', fontWeight: 600, margin: 0 }}>Member</dt>
                <dd style={{ fontSize: 13.5, color: '#0f172a', margin: 0, fontWeight: 600 }}>
                  {paymentResultMember ? getMemberName(paymentResultMember) : 'Member'}
                </dd>

                <dt style={{ fontSize: 12.5, color: '#64748b', fontWeight: 600, margin: 0 }}>Order Number</dt>
                <dd style={{ fontSize: 13.5, color: '#0f172a', margin: 0, wordBreak: 'break-all' }}>
                  {paymentResult.orderNumber || '-'}
                </dd>

                <dt style={{ fontSize: 12.5, color: '#64748b', fontWeight: 600, margin: 0 }}>Amount</dt>
                <dd style={{ fontSize: 13.5, color: '#0f172a', margin: 0 }}>
                  ₹{Number(paymentResult.amount || 0)}
                </dd>

                {paymentResult.gatewayReference && (
                  <>
                    <dt style={{ fontSize: 12.5, color: '#64748b', fontWeight: 600, margin: 0 }}>Gateway Reference</dt>
                    <dd style={{ fontSize: 13.5, color: '#0f172a', margin: 0, wordBreak: 'break-all' }}>
                      {paymentResult.gatewayReference}
                    </dd>
                  </>
                )}
              </dl>

              <p style={{ fontSize: 13, color: '#475569', margin: 0, textAlign: 'center' }}>
                {paymentResult.status === 'success'
                  ? 'Membership activated successfully.'
                  : paymentResult.status === 'failed'
                    ? 'The payment did not go through. The membership has not been activated.'
                    : paymentResult.status === 'cancelled'
                      ? 'The payment was cancelled. The membership has not been activated.'
                      : 'Still awaiting confirmation from the payment gateway. This does not affect any activated membership.'}
              </p>
            </div>

            <div className="fine-popup-footer">
              <button type="button" className="btn btn-light" onClick={closePaymentResultModal}>
                Close
              </button>

              {paymentResult.status === 'pending' && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleModalCheckStatus}
                    disabled={checkingPaymentStatus}
                  >
                    {checkingPaymentStatus ? 'Checking...' : 'Check Status'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handlePaymentResultCollect}
                    disabled={!paymentResultMember}
                  >
                    Collect Payment
                  </button>
                </>
              )}

              {(paymentResult.status === 'failed' || paymentResult.status === 'cancelled') && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handlePaymentResultCollect}
                  disabled={!paymentResultMember}
                >
                  Retry Payment
                </button>
              )}

              {paymentResult.status === 'success' && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      closePaymentResultModal();
                      navigate(`${sectionBase}/payments`);
                    }}
                  >
                    View Payment
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      if (!paymentResultMember) return;
                      closePaymentResultModal();
                      handlePrintReceipt(paymentResultMember);
                    }}
                    disabled={!paymentResultMember || !getSuccessfulPayment(paymentResultMember || {})}
                  >
                    Print Receipt
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {receiptTarget && (
        <div
          className="fine-popup-overlay"
          onMouseDown={(e) => e.target === e.currentTarget && closeReceiptModal()}
        >
          <div className="fine-popup-box mem-receipt-modal">
            <div className="fine-popup-header no-print">
              <h5 className="mb-0">Payment Receipt</h5>
              <button type="button" className="fine-popup-close" onClick={closeReceiptModal}>
                ×
              </button>
            </div>

            <div className="fine-popup-body mem-receipt-body">
              {receiptLoading ? (
                <Loader />
              ) : receiptError ? (
                <div className="mem-receipt-error">{receiptError}</div>
              ) : receiptData ? (
                <div className="receipt-print-area">
                  <div className="receipt-box mem-receipt-box">
                    <div className="receipt-title mem-receipt-title">
                      <div className="mem-receipt-library">
                        <span className="mem-receipt-logo">
                          <i className="fas fa-book" />
                        </span>
                        <span className="mem-receipt-library-name">
                          {receiptData.library_name || 'Library'}
                        </span>
                      </div>
                      <h3 className="lib-receipt-heading">Payment Receipt</h3>
                      <div className="lib-receipt-detail lib-ref-text">
                        Receipt No: LIB-PAY-{receiptData.id}
                      </div>
                    </div>

                    <table className="table table-bordered lib-receipt-detail mem-receipt-table">
                      <tbody>
                        <tr>
                          <th>Member Name</th>
                          <td>{receiptData.member_name || getMemberName(receiptTarget)}</td>
                        </tr>
                        <tr>
                          <th>Membership No</th>
                          <td>{receiptData.membership_no || receiptTarget.membership_no || '-'}</td>
                        </tr>
                        <tr>
                          <th>Payment Purpose</th>
                          <td className="text-capitalize">
                            {String(receiptData.payment_type || '-').replace(/_/g, ' ')}
                          </td>
                        </tr>
                        <tr>
                          <th>Order Number</th>
                          <td className="lib-ref-text">{receiptData.internal_order_id || '-'}</td>
                        </tr>
                        <tr>
                          <th>HDFC Reference</th>
                          <td className="lib-ref-text">
                            {receiptData.gateway_order_id || receiptData.transaction_ref || '-'}
                          </td>
                        </tr>
                        <tr>
                          <th>Amount</th>
                          <td>₹ {Number(receiptData.amount || 0)}</td>
                        </tr>
                        <tr>
                          <th>Payment Method</th>
                          <td className="text-capitalize">{receiptData.payment_mode || '-'}</td>
                        </tr>
                        <tr>
                          <th>Payment Status</th>
                          <td className="text-capitalize">{receiptData.payment_status || '-'}</td>
                        </tr>
                        <tr>
                          <th>Payment Date</th>
                          <td>
                            {receiptData.paid_at || receiptData.created_at
                              ? String(receiptData.paid_at || receiptData.created_at).slice(0, 10)
                              : '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="fine-popup-footer no-print">
              <button type="button" className="btn btn-light" onClick={closeReceiptModal}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handlePrintReceiptNow}
                disabled={!receiptData}
              >
                <i className="fas fa-print me-1" />
                Print
              </button>
            </div>
          </div>
        </div>
      )}

      <RegisterMemberModal
        open={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onRegistered={fetchMembers}
        triggerRef={registerTriggerRef}
      />
    </div>
  );
};

export default MemberList;
